import { Component, HostListener, OnInit, OnDestroy, ViewChild, Inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, interval, lastValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { IdleLogoutModalComponent } from './modules/account/auth/idle-logout-modal/idle-logout-modal.component';
import {
  DeviceSize,
  AccountInfoService,
  AlertsService,
  CompanyService,
  DeviceService,
  SessionInfoService,
  UrlService,
  UserAccountService,
  UtilitiesService,
  AuthDataModel,
  AccountInfo,
  SessionKey,
  NotificationDisplay,
} from 'degarmo-lib';
import Swal from 'sweetalert2';
import packageJson from '../../package.json';

import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { InteractionStatus, EventMessage, EventType } from '@azure/msal-browser';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('screen', { static: true }) screen: any;
  previousUrl: string = null;
  currentUrl: string = null;
  nextUrl: string = null;

  userActivity: any;
  userInactive: Subject<any> = new Subject();

  // decides how long it takes to show the inactivity modal
  inactiveTimerInMinutes: number = 5;
  // the amount of time (in minute) to determine how long the modal can be open before logging out automatically
  minutesToLogoutInModal: number = 1;
  tokenRefreshIntervalInMilliSeconds: number = 600000; // milliseconds
  tokenRefreshInterval: Observable<number>;
  tokenRefreshIntervalSubscription: Subscription;
  modalRef: any;

  private readonly _destroying$ = new Subject<void>();
  loginDisplay = false;
  isIframe = false;

  constructor(
    private accountInfoService: AccountInfoService,
    public alertsService: AlertsService,
    private urlService: UrlService,
    private deviceService: DeviceService,
    private router: Router,
    private modalService: NgbModal,
    private userAccountService: UserAccountService,
    private sessionInfoService: SessionInfoService,
    private utilitiesService: UtilitiesService,
    private companyService: CompanyService,
    private authService: MsalService,
    private broadcastService: MsalBroadcastService,
    private activatedRoute: ActivatedRoute,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) {}

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;
    this.authService.instance.enableAccountStorageEvents(); // Recommended for multi-tab/window sync

    // set browser name
    this.deviceService.getBrowserName();
    this.checkUserInfo();
    this.setBaseURLFromParams();
    this.manageSignedOutInOtherTabs();
    this.addLoginStatusSubscription();
    this.addUserInactiveSubscription();
    this.addRouterEventSubscriptions();

    this.broadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        //console.log('MSAL Interaction Status: None');
        this.setLoginDisplay();
        this.checkAndSetActiveAccount(); //Ensure account is active after login
      });

    this.broadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
          /*(msg: EventMessage) =>
            // Filter for LOGIN or ACQUIRE_TOKEN success events specifically occurring during redirect
            (msg.eventType === EventType.HANDLE_REDIRECT_END ||
              msg.eventType === EventType.LOGIN_SUCCESS ||
              msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
            msg.interactionType === InteractionType.Redirect // Ensure it's from a redirect flow*/
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        //console.log('Redirect Login/Token Success Event Received:', result);
        const activeAccount = this.authService.instance.getActiveAccount() || this.authService.instance.getAllAccounts()[0];

        if (activeAccount) {
          const idTokenClaims = activeAccount.idTokenClaims;
          const email = idTokenClaims?.['email'] as string;
          const roles = idTokenClaims?.['roles'] as string[];

          // Use setTimeout to ensure navigation happens in the next JS cycle,
          // potentially helping avoid race conditions with MSAL internal navigation attempts.
          setTimeout(() => {
            if (email) {
              (async () => {
                try {
                  //this.alertsService.showLoading(true, 1, 'Logging in...');
                  //console.log('Logging in with email:', email);
                  const res: boolean = await lastValueFrom(this.userAccountService.logInUser(email, 'MmPqmBjqRaBrh8vj') as any);
                  if (res === true) {
                    this.router.navigate([this.accountInfoService.data.account.landingRoute]);
                  } else if (typeof res === 'object') {
                    this.router.navigate([this.accountInfoService.data.account.landingRoute]);
                  }
                } catch {
                  this.alertsService.error('There was an error in logging in.');
                  this.alertsService.doneLoading();
                } finally {
                  this.alertsService.doneLoading();
                }
              }).bind(this)();
            }
          }, 0);
        } else {
          console.log('User is not logged in or no active account found after interaction.');
        }
      });

    this.broadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        // Handle login failures if necessary
        console.error('Login/Token acquisition failure:', result);
      });

    this.handleWindowResize();
    window.onresize = this.handleWindowResize;
    (async () => {
      try {
        if (!this.urlService.isLocalHost()) await this.checkVersion();

        await lastValueFrom(this.loadCompanyImages());

        //console.log('handleLegacyURL called if needed');
        //console.log('Current location:', location.href);
        if (location.hash.indexOf('#') !== -1) {
          // Clear interview guide related data from localStorage
          this.sessionInfoService.removeConstant(SessionKey.InterviewGuideData);
          this.sessionInfoService.removeConstant(SessionKey.InterviewGuideMetaData);
          this.sessionInfoService.removeConstant(SessionKey.InterviewGuideRules);
          localStorage.removeItem('interviewGuideData');
          localStorage.removeItem('interviewGuideMetaData');
          localStorage.removeItem('interviewGuideRules');
          //console.log('Handling legacy URL:', location.hash);
          this.handleLegacyURL(location.hash);
        }
      } catch (err) {
        this.alertsService.error(err as string, 'Initialization Error');
        this.userAccountService.logOutUser();
      }
    })();
  }

  /**
   * Sets the base URL for the application from query parameters.
   * This is particularly useful for development environments where the SQL parameter might be passed in the URL.
   * It checks if the host is localhost and if the SQL parameter is present in the query parameters.
   * If the SQL parameter is found, it sets the base URL in the local storage via session info service.
   * @returns
   */
  private setBaseURLFromParams() {
    const sqlParam = 'sql';
    const url = '/account/login';

    if (
      !this.urlService.isLocalHost() ||
      window.location.pathname !== url || // If not on login page, no need to set base URL from params
      this.accountInfoService.getUserLoginInfo() // If user is already logged in, no need to set base URL from params
    ) {
      return;
    }

    this.activatedRoute.queryParams.subscribe((params) => {
      const sqlParamValue = params[sqlParam];
      if (sqlParamValue && sqlParamValue !== '') {
        this.sessionInfoService.setBaseURL(sqlParamValue.toLowerCase());
        this.router.navigate([], {
          queryParams: { [sqlParam]: null },
          queryParamsHandling: 'merge', // merge with existing params
          replaceUrl: true, // replace the current URL without adding a new entry in the history stack
        });
      }
    });
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts detected, sets the first account to active.
     * This reduces load times for scenarios with multiple accounts stored in cache.
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      // Optionally add logic to choose the correct account if multiple exist
      this.authService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0]; // Update activeAccount
      //console.log('Active account set:', activeAccount);
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  checkVersion = async () => {
    const currentVersionNumber: string = await lastValueFrom(this.utilitiesService.getCurrentProjectVersion(1) as any);

    if (packageJson.version.trim() !== currentVersionNumber.trim()) {
      // force the browser to refresh
      window.location.reload();
    }
  };

  addRouterEventSubscriptions = () => {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.previousUrl = this.currentUrl;
      this.currentUrl = event.url;

      // Handle default navigation only if on root path with no hash URL
      if (event.url === '/' && !location.hash) {
        if (this.accountInfoService?.data?.account?.isAuthenticated) {
          this.router.navigate(['/dashboards/participants']);
        } else {
          this.router.navigate(['/account/login']);
        }
      }

      this.urlService._previousUrl = this.previousUrl;
      this.urlService._currentUrl = this.currentUrl;
      this.urlService.setPreviousUrl(this.previousUrl);
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe((event: NavigationStart) => {
      this.nextUrl = event.url;

      this.urlService._nextUrl = this.nextUrl;
      this.urlService.setNextUrl(this.nextUrl);
    });
  };

  addLoginStatusSubscription = () => {
    this.userAccountService.subscribeToLoginChanged((loggedIn: boolean) => {
      if (loggedIn) {
        this.setIdleTimer();
        const authInfo: AuthDataModel = this.sessionInfoService.getConstant(SessionKey.AuthInformation, 'object', false);

        if (!this.tokenRefreshInterval && authInfo.isLoggedIn) {
          this.tokenRefreshInterval = interval(this.tokenRefreshIntervalInMilliSeconds);
          this.tokenRefreshIntervalSubscription = this.tokenRefreshInterval.subscribe({
            next: (_) => {
              this.updateToken();
            },
          });
        }
      } else {
        this.tokenRefreshIntervalSubscription?.unsubscribe();
        delete this.tokenRefreshInterval;
        delete this.tokenRefreshIntervalSubscription;
      }
    });
  };

  addUserInactiveSubscription() {
    this.userInactive.subscribe(() => {
      if (this.accountInfoService.data.account && this.accountInfoService.data.account.isAuthenticated) {
        // open modal
        if (!this.modalRef) {
          this.modalRef = this.modalService.open(IdleLogoutModalComponent, {
            size: 'lg',
            centered: true,
          });
          this.modalRef.componentInstance.minutesToLogout = this.minutesToLogoutInModal;
          this.modalRef.dismissed.subscribe({
            next: (res) => {
              this.modalRef = null;
              clearTimeout(this.userActivity);

              this.setIdleTimer();
              if (res && res.logout) {
                this.userAccountService.logOutUser();
              }

              if (res && res.continue) {
                this.updateToken();
              }

              if (res && res.showCloseModal) {
                // close swal
                Swal.close();

                this.modalRef = this.modalService.open(IdleLogoutModalComponent, {
                  size: 'lg',
                  centered: true,
                  animation: false,
                });

                this.modalRef.componentInstance.minutesToLogout = 0;
                this.modalRef.componentInstance.description = 'You were signed out due to inactivity.';
                this.modalRef.componentInstance.loggedOut = true;

                this.modalRef.dismissed.subscribe({
                  next: () => {
                    delete this.modalRef;
                  },
                });
              } else {
                delete this.modalRef;
              }
            },
          });
        }
      }
    });
  }

  checkUserInfo = () => {
    // check if the session contains privateKey. If not, set it
    if (!localStorage.getItem(SessionKey.PrivateKey)) localStorage.setItem(SessionKey.PrivateKey, this.utilitiesService.createID(500));

    let authInfo: AuthDataModel = this.sessionInfoService.getConstant(SessionKey.AuthInformation, 'object', false);
    const account: AccountInfo = this.sessionInfoService.getConstant(SessionKey.AccountData, 'object', true);

    if (authInfo && authInfo.info && account) {
      this.accountInfoService.data = account;
      this.accountInfoService.loadUserLoginInfo();
      this.userAccountService.broadcastLoginChanged(this.accountInfoService.data.account.isAuthenticated);
    }
  };

  /**
   * Updates the token
   */
  updateToken = () => {
    const authData: AuthDataModel = this.sessionInfoService.getConstant(SessionKey.AuthInformation, 'object', false);
    if (authData?.refreshToken && authData?.isLoggedIn) {
      const token = `refresh_token=${authData.refreshToken}&grant_type=refresh_token`;

      this.userAccountService.getToken(token).subscribe({
        next: (authInfo: AuthDataModel) => {
          if (authInfo.accessToken && authInfo.refreshToken) {
            authData.accessToken = authInfo.accessToken;
            authData.expiresIn = authInfo.expiresIn;
            authData.refreshToken = authInfo.refreshToken;
            authData.tokenType = authInfo.tokenType;
            this.sessionInfoService.setConstant(SessionKey.AuthInformation, authData, true);
          }
        },
      });
    }
  };

  loadCompanyImages = () => {
    return new Observable((subscriber) => {
      (async () => {
        // let imagePath: string = '/assets/images/';
        let imagePath: string = 'assets/images/';
        const host = window.location.host;

        if (
          this.sessionInfoService.getConstant(SessionKey.CompanyLogo, 'object', false) &&
          host === this.sessionInfoService.getConstant(SessionKey.CompanyLogo, 'object', false).host
        ) {
          this.accountInfoService.logo = this.sessionInfoService.getConstant(SessionKey.CompanyLogo, 'object', false);
          subscriber.next(undefined);
          subscriber.complete();
        }

        if (host.includes('localhost')) {
          this.accountInfoService.logo = {
            headerLogoURL: `${imagePath}degarmoHeaderLogo.png`,
            mainLogoURL: `${imagePath}degarmoLogo.png`,
            smLogo: `${imagePath}degarmo_logo_sm.png`,
            loginText: `Log in here to access DeGarmo's Talent Platform.`,
            host: host,
          };
          this.sessionInfoService.setConstant(SessionKey.CompanyLogo, this.accountInfoService.logo, false);
          subscriber.next(undefined);
          subscriber.complete();
        }

        try {
          const degarmoHostNames: string[] = ['dashboard.degarmo.com', 'dashboard.dgstaging.com', 'dashboard.degarmotest.com'];

          if (degarmoHostNames.indexOf(host) !== -1 || host.includes('localhost')) {
            this.accountInfoService.logo = {
              headerLogoURL: `${imagePath}degarmoHeaderLogo.png`,
              mainLogoURL: `${imagePath}degarmoLogo.png`,
              smLogo: `${imagePath}degarmo_logo_sm.png`,
              loginText: `Log in here to access DeGarmo's Talent Platform.`,
              host: host,
            };
            this.sessionInfoService.setConstant(SessionKey.CompanyLogo, this.accountInfoService.logo, false);
            subscriber.next(undefined);
            subscriber.complete();
          } else {
            this.accountInfoService.logo = await lastValueFrom(this.companyService.getCompanyCustomThemeByDomain(host) as any);
            this.sessionInfoService.setConstant(SessionKey.CompanyLogo, this.accountInfoService.logo, false);
            subscriber.next(undefined);
            subscriber.complete();
          }
        } catch {
          this.accountInfoService.logo = {
            headerLogoURL: `${imagePath}degarmoHeaderLogo.png`,
            mainLogoURL: `${imagePath}degarmoLogo.png`,
            smLogo: `${imagePath}degarmo_logo_sm.png`,
            loginText: `Log in here to access DeGarmo's Talent Platform.`,
            host: host,
          };
          this.sessionInfoService.setConstant(SessionKey.CompanyLogo, this.accountInfoService.logo, false);
          subscriber.next(undefined);
          subscriber.complete();
        }
      })();
    });
  };

  manageSignedOutInOtherTabs = () => {
    // detect if the user is logged out in another tab, if so it logs out in all other tabs
    window.addEventListener(
      'storage',
      (e) => {
        const privateKey = localStorage.getItem('privateKey');

        if (privateKey === undefined || privateKey === null) {
          this.router.navigate(['/account/login']);
          this.alertsService.doneLoading(true);
        }
      },
      false
    );
  };

  handleWindowResize = () => {
    const deviceSize: DeviceSize = {
      height: window.innerHeight,
      width: window.innerWidth,
    };

    this.deviceService.setDeviceSize(deviceSize);
  };

  handleLegacyURL = (hash: string) => {
    let newPath: string = '';
    const url = hash.toLowerCase();

    if (url.indexOf('#/interview/interview-guide/digital/start') !== -1) {
      newPath = 'interview-guide-with-params';
    } else if (url.indexOf('#/interview/interview-guide/document/start') !== -1) {
      newPath = 'interview-data-entry-lookup';
    } else if (url.indexOf('#/interview/interview-guide/preview') !== -1) {
      newPath = 'interview-guide-with-params/preview';
    } else if (url.indexOf('#/interview/interview-guide-download') !== -1) {
      newPath = 'interview-guide-download';
    } else if (url.indexOf('#/talent/candidate-lookup') !== -1) {
      newPath = 'talent/participant/lookup';
    } else if (url.indexOf('#/index/password-reset') !== -1) {
      newPath = 'account/password-reset';
    }

    if (newPath) this.router.navigate([newPath], { queryParams: this.urlService.getUrlParams(hash) });
  };

  setIdleTimer = () => {
    this.userActivity && clearTimeout(this.userActivity);
    this.userActivity = setTimeout(() => {
      this.userInactive.next(undefined);
    }, this.inactiveTimerInMinutes * 60 * 1000);
  };

  @HostListener('click', ['$event'])
  @HostListener('keydown', ['$event'])
  @HostListener('input', ['$event'])
  @HostListener('window:mousemove', ['$event'])
  refreshUserStat() {
    clearTimeout(this.userActivity);
    this.setIdleTimer();
  }
}
